import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { stripeService } from '../../services/stripeService';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import PaymentMethods from '../billing-dashboard/components/PaymentMethods';
import InvoiceHistory from '../billing-dashboard/components/InvoiceHistory';






const AccountSettings = () => {
  const { user, userProfile, updateProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('subscription');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [profileForm, setProfileForm] = useState({
    full_name: '',
    company_name: '',
    phone: ''
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [reactivateLoading, setReactivateLoading] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const isPilotMode = stripeService?.isPilotMode(userProfile);
  const pilotExpiration = stripeService?.getPilotModeExpiration(userProfile);

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/agent-dashboard' },
    { label: 'Account', path: '/account' }
  ];

  const tabs = [
    { id: 'subscription', label: 'Subscription', icon: 'CreditCard' },
    { id: 'profile', label: 'Profile', icon: 'User' }
  ];

  useEffect(() => {
    // Check for checkout success/cancel in URL
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams?.get('tab');
    if (tabParam) {
      setActiveTab(tabParam);
    }
    
    if (urlParams?.get('success') === 'true') {
      setSuccessMessage('Subscription activated successfully! Welcome to Pro.');
      setActiveTab('subscription');
      window.history?.replaceState({}, '', '/account');
    } else if (urlParams?.get('canceled') === 'true') {
      setError('Checkout canceled. You can try again anytime.');
      setActiveTab('subscription');
      window.history?.replaceState({}, '', '/account');
    }
  }, []);

  useEffect(() => {
    if (user) {
      loadAccountData();
    }
  }, [user]);

  useEffect(() => {
    if (userProfile) {
      setProfileForm({
        full_name: userProfile?.full_name || '',
        company_name: userProfile?.company_name || '',
        phone: userProfile?.phone || ''
      });
    }
  }, [userProfile]);

  const loadAccountData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [plansResult, paymentMethodsResult, invoicesResult] = await Promise.all([
        stripeService?.getSubscriptionPlans(),
        stripeService?.getPaymentMethods(user?.id),
        stripeService?.getInvoices(user?.id)
      ]);

      if (plansResult?.error) throw new Error(plansResult.error);
      if (paymentMethodsResult?.error) throw new Error(paymentMethodsResult.error);
      if (invoicesResult?.error) throw new Error(invoicesResult.error);

      // Store plans in state for later use
      console.log('📦 Fetched subscription plans:', plansResult?.data);
      console.log('📊 Plans breakdown:');
      plansResult?.data?.forEach(plan => {
        console.log(`  - ${plan?.name}: id=${plan?.id}, tier=${plan?.tier}, billing_period=${plan?.billing_period}, stripe_price_id=${plan?.stripe_price_id}`);
      });
      setSubscriptionPlans(plansResult?.data || []);
      setPaymentMethods(paymentMethodsResult?.data || []);
      setInvoices(invoicesResult?.data || []);
    } catch (err) {
      console.error('Load account data error:', err);
      setError(err?.message || 'Failed to load account data');
    } finally {
      setLoading(false);
    }
  };

  const handlePlanChange = async (planId) => {
    const { error } = await stripeService?.changeSubscription(user?.id, planId);
    if (!error) {
      await loadAccountData();
    }
  };

  const handlePaymentMethodUpdate = async () => {
    await loadAccountData();
  };

  const handleProfileUpdate = async (e) => {
    e?.preventDefault();
    setProfileSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const { error: updateError } = await updateProfile(profileForm);
      if (updateError) throw new Error(updateError?.message);
      setSuccessMessage('Profile updated successfully!');
    } catch (err) {
      console.error('Profile update error:', err);
      setError(err?.message || 'Failed to update profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const renderSubscriptionTab = () => {
    const currentTier = userProfile?.subscription_tier || 'free';
    const subscriptionStatus = userProfile?.subscription_status || 'inactive';
    const subscriptionEndDate = userProfile?.subscription_end_date;
    const billingPeriod = userProfile?.subscription_billing_period || 'monthly';
    const isPilotMode = stripeService?.isPilotMode(userProfile);
    const pilotExpiration = stripeService?.getPilotModeExpiration(userProfile);
    const isCanceled = subscriptionStatus === 'canceled';

    const handleCancelSubscription = async () => {
      if (!window.confirm('Are you sure you want to unsubscribe? You\'ll keep access until the end of your billing period, and you can reactivate anytime before then.')) {
        return;
      }

      setCancelLoading(true);
      setError(null);

      const { data, error: cancelError } = await stripeService?.cancelSubscription();

      if (cancelError) {
        setError(cancelError);
        setCancelLoading(false);
        return;
      }

      setSuccessMessage('Subscription canceled. You\'ll keep access until ' + stripeService?.formatDate(subscriptionEndDate));
      window.location?.reload();
    };

    const handleReactivateSubscription = async () => {
      setReactivateLoading(true);
      setError(null);

      const { data, error: reactivateError } = await stripeService?.reactivateSubscription();

      if (reactivateError) {
        setError(reactivateError);
        setReactivateLoading(false);
        return;
      }

      setSuccessMessage('Subscription reactivated successfully!');
      window.location?.reload();
    };

    const handleUpgradePlan = async (planType) => {
      setUpgradeLoading(planType);
      setError(null);

      try {
        console.log('🔍 handleUpgradePlan called with:', planType);
        console.log('📋 Current subscriptionPlans state:', subscriptionPlans);
        console.log('📊 subscriptionPlans length:', subscriptionPlans?.length);
        console.log('📦 subscriptionPlans type:', typeof subscriptionPlans);
        console.log('🔎 subscriptionPlans is array?', Array.isArray(subscriptionPlans));

        // Check if plans are loaded
        if (!subscriptionPlans || subscriptionPlans?.length === 0) {
          console.error('❌ No subscription plans loaded yet');
          throw new Error('Subscription plans not loaded. Please refresh the page and try again.');
        }

        // Log all available plans
        console.log('📋 All available plans:');
        subscriptionPlans?.forEach((p, index) => {
          console.log(`  Plan ${index + 1}:`, {
            id: p?.id,
            name: p?.name,
            tier: p?.tier,
            billing_period: p?.billing_period,
            stripe_price_id: p?.stripe_price_id,
            price: p?.price
          });
        });

        // Find the plan from database by billing_period instead of using env variables
        const billingPeriod = planType === 'monthly' ? 'monthly' : 'yearly';
        console.log('🔎 Searching for plan with:', { tier: 'pro', billing_period: billingPeriod });
        
        const plan = subscriptionPlans?.find(p => 
          p?.tier === 'pro' && p?.billing_period === billingPeriod
        );

        console.log('🔎 Found plan:', plan);

        if (!plan) {
          console.error('❌ Plan not found in subscriptionPlans array');
          console.error('  - Looking for: tier=pro, billing_period=' + billingPeriod);
          console.error('  - Available plans:', subscriptionPlans);
          throw new Error(`Pro ${planType} plan not found. Please refresh the page and try again.`);
        }

        // Validate that we have a UUID, not a Stripe price ID
        console.log('🔍 Validating plan.id:', plan?.id);
        console.log('  - Type:', typeof plan?.id);
        console.log('  - Length:', plan?.id?.length);
        console.log('  - Starts with "price_"?', plan?.id?.startsWith('price_'));
        
        if (plan?.id?.startsWith('price_')) {
          console.error('❌ CRITICAL: Database returned Stripe price ID instead of UUID!');
          console.error('  - plan.id:', plan?.id);
          console.error('  - This means the database has incorrect data');
          console.error('  - Expected: UUID format (xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)');
          console.error('  - Received: Stripe price ID (price_...)');
          throw new Error('Invalid plan configuration. Please contact support.');
        }

        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (!uuidRegex?.test(plan?.id)) {
          console.error('❌ CRITICAL: plan.id is not a valid UUID!');
          console.error('  - plan.id:', plan?.id);
          throw new Error('Invalid plan identifier. Please refresh the page.');
        }

        console.log('✅ Using plan UUID from database:', {
          planId: plan?.id,
          planName: plan?.name,
          billingPeriod: plan?.billing_period,
          stripePriceId: plan?.stripe_price_id // For reference only
        });

        const { data, error: checkoutError } = await stripeService?.createCheckoutSession(
          plan?.id, // UUID from subscription_plans.id
          user?.id,
          user?.email,
          null
        );

        if (checkoutError) {
          throw new Error(checkoutError);
        }

        if (data?.url) {
          window.location.href = data?.url;
        } else {
          throw new Error('No checkout URL received');
        }
      } catch (err) {
        console.error('Upgrade error:', err);
        setError(err?.message || 'Failed to start checkout');
        setUpgradeLoading(null);
      }
    };

    return (
      <div className="space-y-6">
        {/* Pilot Mode Banner */}
        {isPilotMode && (
          <div className="bg-primary/10 border border-primary rounded-lg p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                <Icon name="Rocket" size={24} className="text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-foreground mb-2">Pilot Mode Active</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  You're currently enjoying 2 months of Pro access without any credit card required. 
                  This is your chance to explore all premium features!
                </p>
                <div className="flex items-center gap-2 text-sm">
                  <Icon name="Calendar" size={16} className="text-primary" />
                  <span className="text-foreground font-medium">
                    Expires: {pilotExpiration ? stripeService?.formatDate(pilotExpiration) : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Cancellation Warning */}
        {isCanceled && subscriptionEndDate && (
          <div className="bg-warning/10 border border-warning rounded-lg p-6">
            <div className="flex items-start gap-4">
              <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-warning mb-1">Subscription Canceled</h3>
                <p className="text-sm text-muted-foreground mb-1">
                  Your subscription has been canceled. You will have access until:
                </p>
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="Calendar" size={16} className="text-warning" />
                  <span className="text-base font-semibold text-foreground">
                    {stripeService?.formatDate(subscriptionEndDate)}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  You can reactivate anytime before this date to continue your Pro access.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleReactivateSubscription}
                  disabled={reactivateLoading}
                  iconName="RotateCcw"
                  iconPosition="left"
                >
                  {reactivateLoading ? 'Reactivating...' : 'Reactivate Subscription'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Plan Comparison */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Free Plan */}
          <div className="relative">
            {currentTier === 'free' && (
              <div className="absolute -top-3 left-4 z-10">
                <div className="px-3 py-1 bg-primary rounded-full">
                  <span className="text-xs font-semibold text-white">
                    Current Plan{subscriptionEndDate && !isCanceled ? ` • Access through ${stripeService?.formatDate(subscriptionEndDate)}` : ''}
                  </span>
                </div>
              </div>
            )}
            <div className={`bg-card border-2 rounded-lg p-6 ${
              currentTier === 'free' ? 'border-primary' : 'border-border'
            }`}>
              <div className="mb-6">
                <h3 className="text-xl font-bold text-foreground mb-2">Free</h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-foreground">$0</span>
                  <span className="text-muted-foreground">/month</span>
                </div>
              </div>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2">
                  <Icon name="Check" size={20} className="text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-foreground">1 property</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="Check" size={20} className="text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-foreground">2 offers per property</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="X" size={20} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">AI-powered analysis</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="X" size={20} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Offer comparison</span>
                </li>
              </ul>
              {currentTier === 'free' ? (
                <Button variant="outline" className="w-full" disabled>
                  Current Plan
                </Button>
              ) : (
                <div className="text-center text-sm text-muted-foreground">
                  Automatic after cancellation
                </div>
              )}
            </div>
          </div>

          {/* Monthly Plan */}
          <div className="relative">
            {currentTier === 'pro' && billingPeriod === 'monthly' && (
              <div className="absolute -top-3 left-4 z-10">
                <div className="px-3 py-1 bg-primary rounded-full">
                  <span className="text-xs font-semibold text-white">
                    Current Plan{subscriptionEndDate && !isCanceled ? ` • Access through ${stripeService?.formatDate(subscriptionEndDate)}` : ''}
                  </span>
                </div>
              </div>
            )}
            <div className={`bg-card border-2 rounded-lg p-6 ${
              currentTier === 'pro' && billingPeriod === 'monthly' ? 'border-primary' : 'border-border'
            }`}>
              <div className="mb-6">
                <h3 className="text-xl font-bold text-foreground mb-2">Pro Monthly</h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-foreground">$15</span>
                  <span className="text-muted-foreground">/month</span>
                </div>
              </div>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2">
                  <Icon name="Check" size={20} className="text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-foreground">Unlimited properties</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="Check" size={20} className="text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-foreground">Unlimited offers</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="Check" size={20} className="text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-foreground">AI-powered analysis</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="Check" size={20} className="text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-foreground">Offer comparison</span>
                </li>
              </ul>
              {currentTier === 'pro' && billingPeriod === 'monthly' && !isCanceled ? (
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={handleCancelSubscription}
                  disabled={cancelLoading}
                >
                  {cancelLoading ? 'Processing...' : 'Unsubscribe'}
                </Button>
              ) : currentTier === 'free' || (currentTier === 'pro' && billingPeriod === 'yearly') ? (
                <Button 
                  variant="default" 
                  className="w-full"
                  onClick={() => handleUpgradePlan('monthly')}
                  disabled={upgradeLoading === 'monthly'}
                >
                  {upgradeLoading === 'monthly' ? 'Loading...' : 'Select Plan'}
                </Button>
              ) : isCanceled ? (
                <div className="text-center text-sm text-muted-foreground">
                  Access until {stripeService?.formatDate(subscriptionEndDate)}
                </div>
              ) : (
                <Button variant="outline" className="w-full" disabled>
                  Current Plan
                </Button>
              )}
            </div>
          </div>

          {/* Yearly Plan */}
          <div className="relative">
            <div className="absolute -top-3 right-4 z-10">
              <div className="px-3 py-1 bg-success rounded-full">
                <span className="text-xs font-semibold text-white">Save 17%</span>
              </div>
            </div>
            {currentTier === 'pro' && billingPeriod === 'yearly' && (
              <div className="absolute -top-3 left-4 z-10">
                <div className="px-3 py-1 bg-primary rounded-full">
                  <span className="text-xs font-semibold text-white">
                    Current Plan{subscriptionEndDate && !isCanceled ? ` • Access through ${stripeService?.formatDate(subscriptionEndDate)}` : ''}
                  </span>
                </div>
              </div>
            )}
            <div className={`bg-card border-2 rounded-lg p-6 ${
              currentTier === 'pro' && billingPeriod === 'yearly' ? 'border-primary' : 'border-border'
            }`}>
              <div className="mb-6">
                <h3 className="text-xl font-bold text-foreground mb-2">Pro Yearly</h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-foreground">$150</span>
                  <span className="text-muted-foreground">/year</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">$12.50/month when billed annually</p>
              </div>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2">
                  <Icon name="Check" size={20} className="text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-foreground">Unlimited properties</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="Check" size={20} className="text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-foreground">Unlimited offers</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="Check" size={20} className="text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-foreground">AI-powered analysis</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="Check" size={20} className="text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-foreground">Offer comparison</span>
                </li>
              </ul>
              {currentTier === 'pro' && billingPeriod === 'yearly' && !isCanceled ? (
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={handleCancelSubscription}
                  disabled={cancelLoading}
                >
                  {cancelLoading ? 'Processing...' : 'Unsubscribe'}
                </Button>
              ) : currentTier === 'free' || (currentTier === 'pro' && billingPeriod === 'monthly') ? (
                <Button 
                  variant="default" 
                  className="w-full"
                  onClick={() => handleUpgradePlan('yearly')}
                  disabled={upgradeLoading === 'yearly'}
                >
                  {upgradeLoading === 'yearly' ? 'Loading...' : 'Select Plan'}
                </Button>
              ) : isCanceled ? (
                <div className="text-center text-sm text-muted-foreground">
                  Access until {stripeService?.formatDate(subscriptionEndDate)}
                </div>
              ) : (
                <Button variant="outline" className="w-full" disabled>
                  Current Plan
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Subscription Details */}
        {currentTier === 'pro' && subscriptionEndDate && !isCanceled && (
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Icon name="Calendar" size={20} className="text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium text-foreground">Next billing date</p>
                  <p className="text-xs text-muted-foreground">
                    {stripeService?.formatDate(subscriptionEndDate)}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-foreground">
                  {billingPeriod === 'monthly' ? '$15.00' : '$150.00'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {billingPeriod === 'monthly' ? 'per month' : 'per year'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Payment Methods Section */}
        <div className="mt-8">
          <PaymentMethods
            paymentMethods={paymentMethods}
            userId={user?.id}
            onUpdate={loadAccountData}
          />
        </div>

        {/* Invoice History Section */}
        <div className="mt-6">
          <InvoiceHistory
            invoices={invoices}
            onViewAll={() => {}}
          />
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Account Settings - OfferSense</title>
        </Helmet>
        <Header />
        <main className="container mx-auto px-4 py-6 md:py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-muted rounded w-1/4"></div>
            <div className="h-64 bg-muted rounded"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="h-48 bg-muted rounded"></div>
              <div className="h-48 bg-muted rounded"></div>
              <div className="h-48 bg-muted rounded"></div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Account Settings - OfferSense</title>
        <meta name="description" content="Manage your account, subscription, and settings" />
      </Helmet>
      <Header />
      <main className="container mx-auto px-4 py-6 md:py-8">
        <Breadcrumbs items={breadcrumbItems} />

        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
            Account Settings
          </h1>
          <p className="text-muted-foreground text-sm md:text-base">
            Manage your subscription, profile, and account preferences
          </p>
        </div>

        {successMessage && (
          <div className="bg-success/10 border border-success/20 rounded-lg p-4 mb-6 flex items-start gap-3">
            <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-success font-medium">{successMessage}</p>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6 flex items-start gap-3">
            <Icon name="AlertCircle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-destructive font-medium">Error</p>
              <p className="text-destructive/80 text-sm mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-border mb-6">
          <div className="flex gap-1 overflow-x-auto">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                {tab?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'subscription' && renderSubscriptionTab()}

          {activeTab === 'profile' && (
            <div className="max-w-2xl space-y-6">
              <div className="bg-card border border-border rounded-lg p-6">
                <h2 className="text-xl font-semibold text-foreground mb-6">Profile Information</h2>
                <form onSubmit={handleProfileUpdate} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Email Address
                    </label>
                    <Input
                      type="email"
                      value={user?.email || ''}
                      disabled
                      className="bg-muted"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Email cannot be changed. Contact support if needed.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Full Name
                    </label>
                    <Input
                      type="text"
                      value={profileForm?.full_name}
                      onChange={(e) => setProfileForm({ ...profileForm, full_name: e?.target?.value })}
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Company Name
                    </label>
                    <Input
                      type="text"
                      value={profileForm?.company_name}
                      onChange={(e) => setProfileForm({ ...profileForm, company_name: e?.target?.value })}
                      placeholder="Enter your company name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Phone Number
                    </label>
                    <Input
                      type="tel"
                      value={profileForm?.phone}
                      onChange={(e) => setProfileForm({ ...profileForm, phone: e?.target?.value })}
                      placeholder="Enter your phone number"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="submit"
                      variant="default"
                      disabled={profileSaving}
                      iconName={profileSaving ? 'Loader2' : 'Save'}
                      iconClassName={profileSaving ? 'animate-spin' : ''}
                    >
                      {profileSaving ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setProfileForm({
                        full_name: userProfile?.full_name || '',
                        company_name: userProfile?.company_name || '',
                        phone: userProfile?.phone || ''
                      })}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>

              {/* Change Password Section */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h2 className="text-xl font-semibold text-foreground mb-4">Security</h2>
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-2">Password</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Change your account password
                  </p>
                  <Button
                    variant="outline"
                    iconName="Key"
                    onClick={() => window.location.href = '/forgot-password'}
                  >
                    Change Password
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AccountSettings;